/**
 * Insufficient-data state for the Marketer View Briefing (SYR-68).
 *
 * Below ~10 responses a Brand Equity score can't be computed, so the whole
 * Briefing (Overview / Areas / Recommendations) can't be shown. Instead of
 * empty or misleading content we encourage data collection — progress toward
 * the response minimum + share / preview / manage survey actions — styled in
 * the marketer-view's own language (amber primary, slate text, soft cards)
 * rather than the dashboard's chrome.
 */

import React, { useMemo } from 'react';
import { Progress, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ShareAltOutlined,
  EyeOutlined,
  SettingOutlined,
  CopyOutlined,
  LinkOutlined,
  PlusOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSurveysQuery } from '../../../services/nestjs-base-api';
import { Utils } from '../../../utils/utils';
import {
  useMarketerScope,
  periodTypeToShortLabel,
} from '../scope/MarketerScopeContext';
import { useNavReturn } from '../nav/NavReturnContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { setExploringOnboarding } from '../shell/onboardingExplore';
import { Card } from '../atoms';
import { THEME } from '../config/theme';
import { type BrandSurvey, makeCohortNamer, surveyLabel } from '../surveyShare';

/** Below this many responses, BE can't be calculated (matches the dashboard). */
const MINIMUM_RESPONSES = 10;

export interface MarketerInsufficientDataProps {
  /** Responses behind the (failed) calculation, for the progress ring. */
  totalResponses: number;
}

/** Soft-accented icon chip used in each action card header. */
const IconChip: React.FunctionComponent<{ bg: string; color: string; children: React.ReactNode }> = ({
  bg,
  color,
  children,
}) => (
  <span
    style={{
      width: 38,
      height: 38,
      borderRadius: 11,
      background: bg,
      color,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 18,
      flexShrink: 0,
    }}
  >
    {children}
  </span>
);

export const MarketerInsufficientData: React.FunctionComponent<
  MarketerInsufficientDataProps
> = ({ totalResponses }) => {
  const { navigateWithReturn } = useNavReturn();
  const navigate = useNavigate();
  const marketerRoot = useMarketerRoot();
  const intl = useIntl();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { activeBrand, scope, cohorts } = useMarketerScope();

  // When the user is still mid-onboarding (skipped the wizard to explore), the
  // guided "Finish setup" flow is the clear next step — not the survey-creation
  // cards. Returning to the wizard mirrors the shell's "Finish setup" banner.
  const needsOnboarding = !!user?.needsOnboarding;
  const handleFinishSetup = () => {
    setExploringOnboarding(user?.id, false);
    navigate(marketerRoot);
  };

  const { data: surveysData } = useGetSurveysQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );
  const cohortName = useMemo(() => makeCohortNamer(cohorts), [cohorts]);

  // This brand's ACTIVE surveys — defensively match brandId or nested brand.id
  // (the surveys payload populates one or the other).
  const activeSurveys = useMemo<BrandSurvey[]>(() => {
    if (!activeBrand) return [];
    return ((surveysData as BrandSurvey[] | undefined) ?? []).filter(
      (s) => (s.brandId ?? s.brand?.id) === activeBrand.id && s.status === 'active',
    );
  }, [surveysData, activeBrand]);
  const hasActiveSurveys = activeSurveys.length > 0;
  // "Finish setup" only until the first survey exists — once a survey is
  // created, onboarding is effectively done (even if the needsOnboarding flag
  // lingers) and the messaging shifts to collecting responses.
  const showFinishSetup = needsOnboarding && !hasActiveSurveys;
  // Only surface per-survey labels when there's more than one to tell apart.
  const multi = activeSurveys.length > 1;

  // Navigate to the in-shell survey routes via navigateWithReturn so the
  // marketer chrome stays AND a "← back to The Briefing" pill is dropped.
  const fromLabel = intl.formatMessage({
    id: 'marketerView.nav.briefing',
    defaultMessage: 'The Briefing',
  });
  const surveysPath = activeBrand
    ? `${marketerRoot}/${activeBrand.hash}/admin/surveys`
    : '/dashboard/surveys';
  const goSurveys = () => navigateWithReturn(surveysPath, fromLabel);
  const goCreateSurvey = () => navigateWithReturn(`${surveysPath}/create`, fromLabel);

  const progressPercent = Math.min(
    Math.round((totalResponses / MINIMUM_RESPONSES) * 100),
    100,
  );

  const copyLink = async (hash: string) => {
    const ok = await Utils.copyText(Utils.getPublicSurveyUrl(hash));
    if (ok) {
      message.success(
        intl.formatMessage({
          id: 'marketerView.insufficient.linkCopied',
          defaultMessage: 'Survey link copied',
        }),
      );
    }
  };

  const primaryBtn: React.CSSProperties = {
    border: 'none',
    background: THEME.primary,
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    padding: '10px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };
  const ghostBtn: React.CSSProperties = {
    ...primaryBtn,
    background: '#fff',
    color: THEME.textBase,
    border: `1px solid ${THEME.border}`,
  };

  return (
    <div>
      {/* Hero — progress toward the response minimum + the encouraging line. */}
      <Card style={{ marginBottom: 18, textAlign: 'center', padding: '40px 32px' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: THEME.textBase, letterSpacing: '-0.02em' }}>
          {activeBrand?.name ?? 'Your brand'}
          <span style={{ color: THEME.textSecondary, fontWeight: 500 }}>
            {' · '}
            <FormattedMessage
              id="marketerView.insufficient.snapshot"
              defaultMessage="Brand Equity snapshot"
            />
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', margin: '22px 0 18px' }}>
          <Progress
            type="circle"
            percent={progressPercent}
            size={140}
            strokeColor={THEME.primary}
            trailColor="#F1F5F9"
            format={() => (
              <div style={{ width: 92, margin: '0 auto' }}>
                <div style={{ fontSize: 30, fontWeight: 800, color: THEME.textBase, lineHeight: 1 }}>
                  {totalResponses}
                </div>
                <div style={{ fontSize: 11, color: THEME.textSecondary, lineHeight: 1.3, marginTop: 5 }}>
                  <FormattedMessage
                    id="marketerView.insufficient.ofMin"
                    defaultMessage="of {min} responses needed"
                    values={{ min: MINIMUM_RESPONSES }}
                  />
                </div>
              </div>
            )}
          />
        </div>

        <div style={{ fontSize: 16, fontWeight: 600, color: THEME.textBase, maxWidth: 520, margin: '0 auto' }}>
          {showFinishSetup ? (
            <FormattedMessage
              id="marketerView.insufficient.finishSetupPrompt"
              defaultMessage="Finish setting up your brand to start measuring brand equity"
            />
          ) : hasActiveSurveys ? (
            <FormattedMessage
              id="marketerView.insufficient.shareToCollect"
              defaultMessage="Share your surveys to start collecting customer responses"
            />
          ) : (
            <FormattedMessage
              id="marketerView.insufficient.createToCollect"
              defaultMessage="Create and share your first survey to start measuring brand equity"
            />
          )}
        </div>
        <div style={{ fontSize: 13, color: THEME.textMuted, marginTop: 8 }}>
          <FormattedMessage
            id="marketerView.insufficient.needMin"
            defaultMessage="A Brand Equity score needs at least {min} responses. Showing {period}."
            values={{ min: MINIMUM_RESPONSES, period: periodTypeToShortLabel(scope.periodType) }}
          />
        </div>
        {showFinishSetup && (
          <button
            type="button"
            onClick={handleFinishSetup}
            style={{ ...primaryBtn, maxWidth: 240, margin: '22px auto 0' }}
          >
            <RocketOutlined />
            <FormattedMessage
              id="marketerView.insufficient.finishSetup"
              defaultMessage="Finish setup"
            />
          </button>
        )}
      </Card>

      {/* Action cards — hidden while onboarding isn't finished; "Finish setup"
          in the hero is the single clear next step then. */}
      {!showFinishSetup && (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        {/* Share */}
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <IconChip bg={THEME.primarySoft} color={THEME.primaryDark}>
              <ShareAltOutlined />
            </IconChip>
            <span style={{ fontSize: 16, fontWeight: 700, color: THEME.textBase }}>
              <FormattedMessage id="marketerView.insufficient.shareTitle" defaultMessage="Share your surveys" />
            </span>
          </div>
          <p style={{ fontSize: 13.5, color: THEME.textSecondary, lineHeight: 1.5, margin: 0, minHeight: 40 }}>
            <FormattedMessage
              id="marketerView.insufficient.shareDesc"
              defaultMessage="Send your survey links to your customers to start collecting brand equity data."
            />
          </p>
          {hasActiveSurveys ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 'auto' }}>
              {activeSurveys.map((s) => (
                <div key={s.hash} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {multi && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: THEME.textBase }}>
                      {surveyLabel(s, cohortName)}
                    </span>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0 8px 0 10px',
                      height: 38,
                      background: THEME.bgTinted,
                      border: `1px solid ${THEME.border}`,
                      borderRadius: 9,
                    }}
                  >
                    <LinkOutlined style={{ color: THEME.textMuted, fontSize: 13, flexShrink: 0 }} />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 11.5,
                        fontFamily: 'monospace',
                        color: THEME.textSecondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={Utils.getPublicSurveyUrl(s.hash)}
                    >
                      {Utils.getPublicSurveyUrl(s.hash)}
                    </span>
                    <button
                      type="button"
                      onClick={() => copyLink(s.hash)}
                      title={intl.formatMessage({
                        id: 'marketerView.insufficient.copy',
                        defaultMessage: 'Copy link',
                      })}
                      style={{
                        flexShrink: 0,
                        border: 'none',
                        background: THEME.primary,
                        color: '#fff',
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        cursor: 'pointer',
                      }}
                    >
                      <CopyOutlined />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={goCreateSurvey}
              style={{ ...primaryBtn, marginTop: 'auto' }}
            >
              <PlusOutlined />
              <FormattedMessage id="marketerView.insufficient.createSurvey" defaultMessage="Create a survey" />
            </button>
          )}
        </Card>

        {/* Preview */}
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <IconChip bg="#D1FAE5" color="#047857">
              <EyeOutlined />
            </IconChip>
            <span style={{ fontSize: 16, fontWeight: 700, color: THEME.textBase }}>
              <FormattedMessage id="marketerView.insufficient.previewTitle" defaultMessage="Preview your survey" />
            </span>
          </div>
          <p style={{ fontSize: 13.5, color: THEME.textSecondary, lineHeight: 1.5, margin: 0, minHeight: 40 }}>
            <FormattedMessage
              id="marketerView.insufficient.previewDesc"
              defaultMessage="See exactly what your customers experience when they complete your survey."
            />
          </p>
          {/* Mirror the Share card's per-survey blocks (label + 38px control)
              so each Open preview lines up with its share-link row. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 'auto' }}>
            {hasActiveSurveys ? (
              activeSurveys.map((s) => (
                <div key={s.hash} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {multi && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: THEME.textBase }}>
                      {surveyLabel(s, cohortName)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => window.open(Utils.getTestSurveyUrl(s.hash), '_blank')}
                    style={{ ...ghostBtn, height: 38, padding: '0 16px' }}
                    title={surveyLabel(s, cohortName)}
                  >
                    <RocketOutlined />
                    <FormattedMessage id="marketerView.insufficient.openPreview" defaultMessage="Open preview" />
                  </button>
                </div>
              ))
            ) : (
              <button
                type="button"
                onClick={goCreateSurvey}
                style={ghostBtn}
              >
                <PlusOutlined />
                <FormattedMessage id="marketerView.insufficient.createSurvey" defaultMessage="Create a survey" />
              </button>
            )}
          </div>
        </Card>

        {/* Manage */}
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <IconChip bg="#EEF4FF" color="#3B6FD4">
              <SettingOutlined />
            </IconChip>
            <span style={{ fontSize: 16, fontWeight: 700, color: THEME.textBase }}>
              <FormattedMessage id="marketerView.insufficient.manageTitle" defaultMessage="Manage surveys" />
            </span>
          </div>
          <p style={{ fontSize: 13.5, color: THEME.textSecondary, lineHeight: 1.5, margin: 0, minHeight: 40 }}>
            <FormattedMessage
              id="marketerView.insufficient.manageDesc"
              defaultMessage="View responses, create new surveys, and configure your survey settings."
            />
          </p>
          <button
            type="button"
            onClick={goSurveys}
            style={{ ...ghostBtn, marginTop: 'auto' }}
          >
            <SettingOutlined />
            <FormattedMessage id="marketerView.insufficient.goToSurveys" defaultMessage="Go to surveys" />
          </button>
        </Card>
      </div>
      )}
    </div>
  );
};
